<script setup>
import { computed, ref, watch } from 'vue'
import { useNetworkHealth } from '@/composables/useNetworkHealth'
import { useSyncEngine } from '@/services/syncEngine'

const { isOnline, status, offlineSince, isStale } = useNetworkHealth()
const { pendingCount, isSyncing } = useSyncEngine()

const showRecoveredSnack = ref(false)
const offlineDurationText = ref('')

let durationTimer = null

function formatDuration(ms) {
  const sec = Math.floor(ms / 1000)
  if (sec < 60) return `${sec}s`
  const min = Math.floor(sec / 60)
  const remSec = sec % 60
  return `${min}m ${remSec}s`
}

watch(isOnline, (online, wasOnline) => {
  if (online && wasOnline === false) {
    showRecoveredSnack.value = true
    if (durationTimer) clearInterval(durationTimer)
    durationTimer = null
    offlineDurationText.value = ''
    setTimeout(() => { showRecoveredSnack.value = false }, 5000)
  }

  if (!online) {
    durationTimer = setInterval(() => {
      if (offlineSince.value) {
        offlineDurationText.value = formatDuration(Date.now() - offlineSince.value)
      }
    }, 1000)
  }
})

function hardRefresh() {
  window.location.reload()
}

const bannerIcon = computed(() => {
  if (isStale.value) return 'mdi-alert-circle'
  return 'mdi-wifi-off'
})

const bannerText = computed(() => {
  const pending = pendingCount.value
  const pendingSuffix = pending > 0 ? ` | ${pending} item${pending > 1 ? 's' : ''} saved locally` : ''
  if (isStale.value) {
    return `Connection lost for ${offlineDurationText.value || '5+ min'}. Please reload the page.${pendingSuffix}`
  }
  return `Server unreachable${offlineDurationText.value ? ' for ' + offlineDurationText.value : ''}. Reconnecting...${pendingSuffix}`
})

const recoveredText = computed(() => {
  const pending = pendingCount.value
  if (pending > 0 && isSyncing.value) {
    return `Connection restored. Syncing ${pending} pending item${pending > 1 ? 's' : ''}...`
  }
  return 'Connection restored. Refreshing data...'
})
</script>

<template>
  <v-slide-y-transition>
    <v-banner
      v-if="!isOnline"
      color="error"
      density="compact"
      :icon="bannerIcon"
      class="connection-status-banner"
    >
      <template #text>
        <span class="text-body-2 font-weight-medium">{{ bannerText }}</span>
      </template>
      <template #actions>
        <v-btn
          v-if="isStale"
          variant="outlined"
          size="small"
          color="white"
          @click="hardRefresh"
        >
          Reload Page
        </v-btn>
        <v-progress-circular
          v-else
          indeterminate
          size="16"
          width="2"
          color="white"
        />
      </template>
    </v-banner>
  </v-slide-y-transition>

  <v-snackbar
    v-model="showRecoveredSnack"
    color="success"
    timeout="5000"
    location="top"
  >
    <v-icon start size="18">mdi-wifi-check</v-icon>
    {{ recoveredText }}
  </v-snackbar>
</template>

<style scoped>
.connection-status-banner {
  z-index: 9999;
}
</style>
